import { memo, useMemo, useState } from 'react';
import { Box, Flex, Table, Tbody, Text, Th, Thead, Tr } from '@chakra-ui/react';
import { ChevronLeftIcon, ChevronRightIcon } from '@chakra-ui/icons';
import { useQuery } from '@tanstack/react-query';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';

import { useAuthContext } from 'context/authContext';
import { STATUS } from 'projectConstants';
import { ExportDataRequestBody } from 'models/requestBody/createExportData.interface';
import {
  getBlockDetails,
  getDimension,
  getExpandKey,
  getIndicatorTableStatus,
  getModelDetails,
  getTableData,
  getTableFilter,
  getTableHeaderData,
  overallIndicatorFilter,
  setExpandKey,
  setTableFilter,
} from 'redux/PlanPageSlice';
import { getBaseScenario } from 'redux/ScenarioSlice';
import apiService from 'services';
import { getPlanPageThemeColors } from 'redux/ThemeSlice';
import saveFile, { buildBlockExportFileName } from 'utils/file';
import { AppDispatch } from 'utils/GlobalHelpers';

import {
  PLAN_KPI_SELECTED_BG,
  PLAN_KPI_SELECTED_BORDER,
  PLAN_TABLE_METRIC_COLUMN_MIN_WIDTH,
  PLAN_TEXT,
} from '../planPageRevampUi';

import PlanDataTableRow from './PlanDataTableRow';
import PlanDataTableBodySkeleton from './PlanDataTableBodySkeleton';
import PlanDataTableToolbar from './PlanDataTableToolbar';

import type { PlanTimeFilterValue } from './PlanTimeFilterSegment';

interface Properties {
  onToggleAuthModal: () => void;
  pageScrollMode?: boolean;
}

const PlanDataTable = ({ onToggleAuthModal, pageScrollMode = false }: Properties) => {
  const { blockId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuthContext();
  const dispatch: AppDispatch = useDispatch();
  const tableData = useSelector(getTableData);
  const tableHeaderData = useSelector(getTableHeaderData);
  const tableFilter = useSelector(getTableFilter);
  const expandKey = useSelector(getExpandKey);
  const modelDetail = useSelector(getModelDetails);
  const blockDetails = useSelector(getBlockDetails);
  const dimension = useSelector(getDimension);
  const indicatorFilter = useSelector(overallIndicatorFilter);
  const baseScenario = useSelector(getBaseScenario);
  const themeColors = useSelector(getPlanPageThemeColors);
  const tableStatus = useSelector(getIndicatorTableStatus);
  const [isExporting, setIsExporting] = useState(false);

  const timeAggregation = modelDetail?.time_properties?.time_granularity
    ? String(modelDetail.time_properties.time_granularity).toUpperCase()
    : 'M';

  const visibleHeaders = (tableHeaderData || []).filter((item: any) => item?.isShow);

  const onExpandHeader = (header: any) => {
    if (!header?.isShowExpandable || timeAggregation === 'Y') return;
    if (expandKey !== header.name && (tableFilter === 'Y' || tableFilter === 'Q')) {
      dispatch(setExpandKey(header.name));
    } else {
      dispatch(setExpandKey(''));
    }
  };

  const exportRequestBody: ExportDataRequestBody = useMemo(
    () => ({
      orient: 'table',
      query_params: {
        rows: [{ name: 'Indicators', filter: indicatorFilter }, { name: dimension?.name || null }],
        columns: [{ name: 'Time', display_levels: [tableFilter] }],
        filters: [],
      },
      scenario_id: baseScenario?.id,
    }),
    [baseScenario?.id, dimension?.name, indicatorFilter, tableFilter],
  );

  const { refetch: fetchExportData } = useQuery(
    ['export', blockId, baseScenario?.id, tableFilter],
    () =>
      apiService.create_block_export({
        block_id: Number(blockId),
        request_body: exportRequestBody,
      }),
    {
      enabled: false,
      onError: (response: any) => {
        if (response.response?.data?.message) {
          navigate('/error');
        }
      },
    },
  );

  const onDownload = async () => {
    if (user?.is_anonymous) {
      onToggleAuthModal();
      return;
    }

    setIsExporting(true);
    try {
      const { data: exportData } = await fetchExportData();
      if (exportData) {
        const fileName = buildBlockExportFileName(blockDetails?.name);
        const blob = [exportData.data];
        const contentType =
          exportData.headers?.['content-type'] ??
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
        saveFile(fileName, blob, contentType);
      }
    } finally {
      setIsExporting(false);
    }
  };

  const onTimeFilterChange = (filterKey: PlanTimeFilterValue) => {
    dispatch(setTableFilter(filterKey));
  };

  const isTableLoading =
    tableStatus === STATUS.LOADING || (tableStatus === STATUS.IDLE && !tableData?.length);
  const hasCachedTable = Boolean((tableData as unknown[])?.length);
  const showTableSkeleton = isTableLoading && !hasCachedTable;
  const isTableEmpty =
    tableStatus === STATUS.SUCCESS && (!tableData || (tableData as unknown[]).length === 0);

  const metricColumnStyles = {
    position: 'sticky' as const,
    left: 0,
    zIndex: 3,
    bg: PLAN_KPI_SELECTED_BG,
    py: 3,
    px: 3,
    minW: PLAN_TABLE_METRIC_COLUMN_MIN_WIDTH,
    borderRight: '1px solid',
    borderColor: PLAN_KPI_SELECTED_BORDER,
    ...PLAN_TEXT.tableComfortaa,
    color: themeColors.textDark,
    textTransform: 'none' as const,
    letterSpacing: 'normal',
  };

  const periodHeaderStyles = {
    py: 3,
    px: 5,
    bg: PLAN_KPI_SELECTED_BG,
    borderBottom: '1px solid',
    borderRight: '1px solid',
    borderColor: PLAN_KPI_SELECTED_BORDER,
    ...PLAN_TEXT.tableComfortaa,
    color: themeColors.textDark,
    textTransform: 'none' as const,
    letterSpacing: 'normal',
    textAlign: 'end' as const,
    whiteSpace: 'nowrap' as const,
  };

  return (
    <Flex
      direction='column'
      flex={pageScrollMode ? '1 1 auto' : '1 1 0'}
      minH={pageScrollMode ? undefined : 0}
      minW={0}
      bg={themeColors.surface}
      border='1px solid'
      borderColor={PLAN_KPI_SELECTED_BORDER}
      borderRadius='12px'
      overflow='hidden'
    >
      <PlanDataTableToolbar
        timeAggregation={timeAggregation}
        tableFilter={tableFilter as PlanTimeFilterValue}
        onTimeFilterChange={onTimeFilterChange}
        onDownload={onDownload}
        isExporting={isExporting}
      />

      <Box
        flex={pageScrollMode ? undefined : '1'}
        minH={pageScrollMode ? undefined : 0}
        minW={0}
        overflowX='auto'
        overflowY={pageScrollMode ? 'visible' : 'auto'}
        className={pageScrollMode ? undefined : 'scroll'}
        position='relative'
      >
        {showTableSkeleton && <PlanDataTableBodySkeleton />}
        {!showTableSkeleton && isTableEmpty && (
          <Flex h='200px' alignItems='center' justifyContent='center'>
            <Text {...PLAN_TEXT.body} color={themeColors.textSecondary} textAlign='center'>
              No table data available for the current filters.
            </Text>
          </Flex>
        )}
        {!showTableSkeleton && !isTableEmpty && (
          <Table size='sm' variant='unstyled'>
            <Thead position='sticky' top={0} zIndex={2}>
              <Tr>
                <Th {...metricColumnStyles}>Metric</Th>
                {visibleHeaders.map((item: any, index: number) => {
                  const isExpanded = expandKey === item?.name;
                  const isExpandable = Boolean(item?.isShowExpandable) && timeAggregation !== 'Y';

                  return (
                    <Th
                      key={`${item?.name ?? ''}-${index}`}
                      {...periodHeaderStyles}
                      cursor={isExpandable ? 'pointer' : 'default'}
                      onClick={() => onExpandHeader(item)}
                    >
                      <Flex alignItems='center' justifyContent='flex-end' gap={1}>
                        {isExpandable &&
                          (isExpanded ? (
                            <ChevronRightIcon boxSize={3} color={themeColors.primary} />
                          ) : (
                            <ChevronLeftIcon boxSize={3} color={themeColors.primary} />
                          ))}
                        {item?.name}
                      </Flex>
                    </Th>
                  );
                })}
              </Tr>
            </Thead>
            <Tbody>
              {((tableData as any[]) || []).map((row: any, index: number) => (
                <PlanDataTableRow key={(row?.name ?? '') + index} row={row} expandKey={expandKey} />
              ))}
            </Tbody>
          </Table>
        )}
      </Box>
    </Flex>
  );
};

export default memo(PlanDataTable);
